import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { InteractiveDotBackground } from '../Landing/InteractiveDotBackground';
import { AuthStoryAside } from './AuthStoryAside';
import { AuthNavSwitcher } from './AuthNavSwitcher';
import { pathnameToAuthPageId } from './authPageStories';
import { cn } from '../../utils/cn';

const WIDE_PAGES = new Set(['/register']);

interface AuthLayoutProps {
  children: React.ReactNode;
}

/**
 * Shared auth chrome — fixed viewport, no page scroll.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const pageId = pathnameToAuthPageId(location.pathname);
  const wide = WIDE_PAGES.has(location.pathname);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <motion.div
      key={location.pathname}
      className="relative h-[100dvh] max-h-[100dvh] overflow-hidden bg-white text-gray-900 antialiased"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <InteractiveDotBackground
        className="z-0"
        color="rgba(71, 85, 105, 0.34)"
        accentColor="rgba(37, 99, 235, 0.55)"
      />

      <div className="relative z-10 flex h-full min-h-0 flex-col md:flex-row">
        <aside className="relative hidden h-full min-h-0 shrink-0 overflow-hidden border-r border-slate-100 bg-slate-50/70 md:flex md:w-[42%] lg:w-[44%] xl:w-[46%]">
          <motion.div
            key={`aside-${pageId}`}
            className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden px-5 py-6 lg:px-8"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
          >
            <AuthStoryAside />
          </motion.div>
        </aside>

        <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white/85 backdrop-blur-[2px]">
          <div className="flex shrink-0 items-center justify-between gap-3 px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
              Back to home
            </Link>
            <Link to="/" className="md:hidden hover:opacity-80 transition-opacity">
              <img src="/cmndf-logo.png" alt="cmnd[f]." className="h-8 w-auto object-contain" />
            </Link>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center overflow-hidden px-4 pb-4 sm:px-6 sm:pb-5 lg:px-8 lg:pb-6">
            <div className={cn('mx-auto w-full min-h-0', wide ? 'max-w-lg' : 'max-w-md')}>
              <AuthNavSwitcher className="mb-4 shrink-0" />
              <div key={location.pathname} className="min-h-0 w-full">
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>
    </motion.div>
  );
};
